import type { ReactNode } from 'react';

import { Card } from '@/components/ui/card';
import { ProgressBar } from '@/components/ui/progress-bar';
import { usePreferences } from '@/stores/preferences-store';
import { cn } from '@/utils/cn';
import { formatMoney, formatPercent } from '@/utils/format';

import { MoneyDisplay } from './money-display';

/**
 * Target, raised and how far along — projects and pledges.
 *
 * Both amounts are shown, not only the percentage, because a church board asks "how
 * much is left" far more often than it asks "what percentage".
 */

export interface ProgressCardProps {
  title: string;
  description?: string;
  /** Integer minor units. */
  raised: number;
  target: number;
  currency: string;
  /** "raised of GHS 50,000" — the caller supplies the sentence, we supply the amounts. */
  summary: (parts: { raised: string; target: string; percent: string }) => string;
  footer?: ReactNode;
  progressLabel: string;
  className?: string;
}

export const ProgressCard = ({
  title,
  description,
  raised,
  target,
  currency,
  summary,
  footer,
  progressLabel,
  className,
}: ProgressCardProps) => {
  const locale = usePreferences((state) => state.locale);
  const fraction = target > 0 ? raised / target : 0;

  return (
    <Card className={cn('p-5', className)}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-card-title font-semibold text-fg">{title}</h3>
          {description ? (
            <p className="mt-0.5 text-body-sm text-fg-secondary">{description}</p>
          ) : null}
        </div>
        <MoneyDisplay amount={raised} currency={currency} size="lg" className="shrink-0" />
      </div>

      <ProgressBar
        value={raised}
        max={target}
        label={progressLabel}
        variant={fraction >= 1 ? 'success' : 'brand'}
        className="mt-4"
      />

      <p className="mt-2 text-body-sm text-fg-secondary">
        {summary({
          raised: formatMoney(raised, currency, { locale }),
          target: formatMoney(target, currency, { locale }),
          percent: formatPercent(fraction, locale),
        })}
      </p>

      {footer ? <div className="mt-4">{footer}</div> : null}
    </Card>
  );
};
